/**
 * DraftClausesTable
 *
 * Flat, grouped data table for draft clauses using TanStack Table + shadcn.
 * Stripe-style contained card table with visible column headers, integrated
 * group rows, and compact clause rows.
 */

import {
    IDraftTopicDetail,
    IEnabledClauseSelection,
} from "@/api/clause-checklists/checklist-generation.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    ColumnDef,
    ExpandedState,
    GroupingState,
    Row,
    RowSelectionState,
    getCoreRowModel,
    getExpandedRowModel,
    getGroupedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {
    ChevronDown,
    ChevronRight,
    Plus,
    Sparkles,
    Trash2,
    XCircle,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { CONFIDENCE_ORDER } from "./confidenceUtils";
import { ManageDisabledClausesDialog } from "./ManageDisabledClausesDialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClauseTableRow {
    id: string;
    topicId: number;
    topicName: string;
    topicClauseId: number;
    clauseTitle: string;
    clauseDescription: string;
    rationale: string | null;
    sectionTypes: { id: number | null; name: string; enabled: boolean }[];
    isSentinel?: boolean;
}

export interface TopicMeta {
    topicId: number;
    topicName: string;
    rationale: string | null;
    enabledCount: number;
    totalCount: number;
    hasDisabledClauses: boolean;
}

interface DraftClausesTableProps {
    topics: IDraftTopicDetail[];
    onChange: (topics: IDraftTopicDetail[]) => void;
    onAddTopic: () => void;
    onAddClause: () => void;
    onRemoveTopic?: (topicId: number) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VISIBLE_COL_COUNT = 4;

// ---------------------------------------------------------------------------
// Row derivation
// ---------------------------------------------------------------------------

function deriveRowsAndMeta(topics: IDraftTopicDetail[]) {
    const rows: ClauseTableRow[] = [];
    const topicMetaMap = new Map<number, TopicMeta>();

    const sorted = [...topics].sort((a, b) => {
        const aOrd = a.confidence ? (CONFIDENCE_ORDER[a.confidence] ?? 3) : 3;
        const bOrd = b.confidence ? (CONFIDENCE_ORDER[b.confidence] ?? 3) : 3;
        return aOrd - bOrd;
    });

    for (const topic of sorted) {
        let topicRowCount = 0;
        const enabledClauseIds = new Set(
            topic.enabled_clauses.map((ec) => ec.topic_clause_id)
        );

        for (const clause of topic.all_clauses) {
            const enabledSectionIds = new Set(
                topic.enabled_clauses
                    .filter(
                        (ec) => ec.topic_clause_id === clause.topic_clause_id
                    )
                    .map((ec) => ec.document_section_type_id)
            );

            const sectionTypes =
                clause.document_section_types.length > 0
                    ? clause.document_section_types.map((dst) => ({
                          id: dst.id as number | null,
                          name: dst.name,
                          enabled: enabledSectionIds.has(dst.id),
                      }))
                    : [
                          {
                              id: null as number | null,
                              name: "General",
                              enabled: enabledSectionIds.has(null),
                          },
                      ];

            if (sectionTypes.some((st) => st.enabled)) {
                rows.push({
                    id: `${topic.topic_id}:${clause.topic_clause_id}`,
                    topicId: topic.topic_id,
                    topicName: topic.topic_name,
                    topicClauseId: clause.topic_clause_id,
                    clauseTitle: clause.title,
                    clauseDescription: clause.description,
                    rationale: topic.rationale,
                    sectionTypes,
                });
                topicRowCount++;
            }
        }

        if (topicRowCount === 0) {
            rows.push({
                id: `${topic.topic_id}:sentinel`,
                topicId: topic.topic_id,
                topicName: topic.topic_name,
                topicClauseId: -1,
                clauseTitle: "",
                clauseDescription: "",
                rationale: topic.rationale,
                sectionTypes: [],
                isSentinel: true,
            });
        }

        let totalCount = 0;
        for (const clause of topic.all_clauses) {
            totalCount += Math.max(clause.document_section_types.length, 1);
        }

        topicMetaMap.set(topic.topic_id, {
            topicId: topic.topic_id,
            topicName: topic.topic_name,
            rationale: topic.rationale,
            enabledCount: topic.enabled_clauses.length,
            totalCount,
            hasDisabledClauses: topic.all_clauses.some(
                (c) => !enabledClauseIds.has(c.topic_clause_id)
            ),
        });
    }

    return { rows, topicMetaMap };
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const columns: ColumnDef<ClauseTableRow, unknown>[] = [
    {
        id: "topicId",
        accessorKey: "topicId",
        header: "Topic",
        enableGrouping: true,
    },
    { id: "select", header: "", enableGrouping: false, size: 36 },
    {
        id: "clause",
        accessorKey: "clauseTitle",
        header: "Clause",
        enableGrouping: false,
    },
    {
        id: "sectionTypes",
        accessorKey: "sectionTypes",
        header: "Section Types",
        enableGrouping: false,
        size: 200,
    },
    { id: "actions", header: "", enableGrouping: false, size: 48 },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TopicGroupHeaderRow({
    row,
    meta,
    onManage,
    onRemove,
}: {
    row: Row<ClauseTableRow>;
    meta: TopicMeta | undefined;
    onManage: (topicId: number) => void;
    onRemove: (topicId: number) => void;
}) {
    const isExpanded = row.getIsExpanded();
    const topicName = meta?.topicName ?? "Unknown Topic";
    const enabledCount = meta?.enabledCount ?? 0;
    const totalCount = meta?.totalCount ?? 0;
    const hasDisabled = meta?.hasDisabledClauses ?? false;
    const rationale = meta?.rationale;

    const [rationaleOpen, setRationaleOpen] = useState(false);

    return (
        <>
            {/* Group header row */}
            <TableRow className="border-border/60 bg-muted/40 hover:bg-muted/60 border-b">
                <TableCell
                    colSpan={VISIBLE_COL_COUNT}
                    className="px-0 py-0 whitespace-normal"
                >
                    <div
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 select-none"
                        onClick={() => row.toggleExpanded()}
                        role="button"
                        aria-expanded={isExpanded}
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                row.toggleExpanded();
                            }
                        }}
                    >
                        {isExpanded ? (
                            <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
                        ) : (
                            <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
                        )}

                        <span className="text-foreground flex-1 truncate text-[13px] font-semibold">
                            {topicName}
                        </span>

                        <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                            {enabledCount}/{totalCount}
                        </span>

                        {rationale && (
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <Button
                                            variant="ghost"
                                            size="icon-xs"
                                            className="text-violet-400 hover:bg-violet-50 hover:text-violet-600"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setRationaleOpen((v) => !v);
                                            }}
                                        />
                                    }
                                >
                                    <Sparkles className="size-3" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    {rationaleOpen
                                        ? "Hide rationale"
                                        : "Show AI rationale"}
                                </TooltipContent>
                            </Tooltip>
                        )}

                        {hasDisabled && (
                            <Button
                                variant="ghost"
                                size="xs"
                                className="text-muted-foreground hover:text-foreground hover:bg-muted"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onManage(row.original.topicId);
                                }}
                            >
                                Edit Clauses
                            </Button>
                        )}

                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <Button
                                        variant="ghost"
                                        size="icon-xs"
                                        className="text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(row.original.topicId);
                                        }}
                                    />
                                }
                            >
                                <Trash2 className="size-3.5" />
                            </TooltipTrigger>
                            <TooltipContent>Remove topic</TooltipContent>
                        </Tooltip>
                    </div>
                </TableCell>
            </TableRow>

            {/* Rationale expandable row */}
            {isExpanded && rationale && rationaleOpen && (
                <TableRow className="border-border/40 border-b bg-violet-50/50 hover:bg-violet-50/70">
                    <TableCell
                        colSpan={VISIBLE_COL_COUNT}
                        className="px-4 py-2 pl-10 whitespace-normal"
                    >
                        <div className="flex items-start gap-1.5">
                            <Sparkles className="mt-0.5 size-3 shrink-0 text-violet-400" />
                            <p className="text-[12px] leading-relaxed text-violet-700/80">
                                {rationale}
                            </p>
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

function SentinelRow() {
    return (
        <TableRow className="border-border/40 border-b hover:bg-transparent">
            <TableCell
                colSpan={VISIBLE_COL_COUNT}
                className="whitespace-normal"
            >
                <p className="text-muted-foreground py-3 text-center text-[12px] italic">
                    All clauses disabled — click Edit Clauses to re-enable
                </p>
            </TableCell>
        </TableRow>
    );
}

function ClauseLeafRow({
    row,
    onToggleSectionType,
    onRemoveClause,
}: {
    row: Row<ClauseTableRow>;
    onToggleSectionType: (
        topicId: number,
        topicClauseId: number,
        sectionTypeId: number | null
    ) => void;
    onRemoveClause: (topicId: number, topicClauseId: number) => void;
}) {
    const {
        topicId,
        topicClauseId,
        clauseTitle,
        clauseDescription,
        sectionTypes,
    } = row.original;

    return (
        <TableRow
            data-state={row.getIsSelected() ? "selected" : undefined}
            className="group/row border-border/40 hover:bg-muted/20 border-b transition-colors"
        >
            {/* Select */}
            <TableCell className="w-9 py-2 pr-0 align-middle">
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(v) => row.toggleSelected(!!v)}
                    aria-label={`Select ${clauseTitle}`}
                />
            </TableCell>

            {/* Clause title + description */}
            <TableCell className="py-2 whitespace-normal">
                <p className="text-foreground text-[13px] leading-snug font-medium">
                    {clauseTitle}
                </p>
                {clauseDescription && (
                    <p className="text-muted-foreground mt-0.5 line-clamp-2 text-[11px] leading-snug">
                        {clauseDescription}
                    </p>
                )}
            </TableCell>

            {/* Section type badges */}
            <TableCell className="w-[200px] py-2 whitespace-normal">
                <div className="flex flex-wrap gap-1">
                    {sectionTypes.map((st) => (
                        <Badge
                            key={st.id ?? "general"}
                            variant="outline"
                            className={
                                st.enabled
                                    ? "text-foreground/70 border-border hover:bg-muted cursor-pointer text-[10px] font-normal transition-colors"
                                    : "text-muted-foreground/50 border-border/50 hover:bg-muted/50 cursor-pointer text-[10px] font-normal line-through transition-colors"
                            }
                            onClick={() =>
                                onToggleSectionType(
                                    topicId,
                                    topicClauseId,
                                    st.id
                                )
                            }
                        >
                            {st.name}
                        </Badge>
                    ))}
                </div>
            </TableCell>

            {/* Actions */}
            <TableCell className="w-12 py-2">
                <Button
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground/40 hover:text-destructive transition-colors"
                    onClick={() => onRemoveClause(topicId, topicClauseId)}
                    aria-label="Remove clause"
                >
                    <XCircle className="size-3.5" />
                </Button>
            </TableCell>
        </TableRow>
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DraftClausesTable({
    topics,
    onChange,
    onAddTopic,
    onAddClause,
    onRemoveTopic,
}: DraftClausesTableProps) {
    const { rows, topicMetaMap } = useMemo(
        () => deriveRowsAndMeta(topics),
        [topics]
    );

    const [grouping] = useState<GroupingState>(["topicId"]);
    const [expanded, setExpanded] = useState<ExpandedState>(true);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [managingTopicId, setManagingTopicId] = useState<number | null>(null);
    const manageTopic = topics.find((t) => t.topic_id === managingTopicId);

    const selectedCount = Object.values(rowSelection).filter(Boolean).length;

    const table = useReactTable({
        data: rows,
        columns,
        state: { grouping, expanded, rowSelection },
        onExpandedChange: setExpanded,
        onRowSelectionChange: setRowSelection,
        autoResetExpanded: false,
        getCoreRowModel: getCoreRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        enableRowSelection: (row) =>
            !row.getIsGrouped() && !row.original.isSentinel,
        getRowId: (row) => row.id,
    });

    // -- Interaction callbacks -----------------------------------------------

    const handleToggleSectionType = useCallback(
        (
            topicId: number,
            topicClauseId: number,
            sectionTypeId: number | null
        ) => {
            const updated = topics.map((topic) => {
                if (topic.topic_id !== topicId) return topic;

                const exists = topic.enabled_clauses.some(
                    (ec) =>
                        ec.topic_clause_id === topicClauseId &&
                        ec.document_section_type_id === sectionTypeId
                );

                const enabled_clauses: IEnabledClauseSelection[] = exists
                    ? topic.enabled_clauses.filter(
                          (ec) =>
                              !(
                                  ec.topic_clause_id === topicClauseId &&
                                  ec.document_section_type_id === sectionTypeId
                              )
                      )
                    : [
                          ...topic.enabled_clauses,
                          {
                              topic_clause_id: topicClauseId,
                              document_section_type_id: sectionTypeId,
                          },
                      ];

                return { ...topic, enabled_clauses };
            });

            onChange(updated);
        },
        [topics, onChange]
    );

    const handleRemoveClause = useCallback(
        (topicId: number, topicClauseId: number) => {
            const updated = topics.map((topic) => {
                if (topic.topic_id !== topicId) return topic;
                return {
                    ...topic,
                    enabled_clauses: topic.enabled_clauses.filter(
                        (ec) => ec.topic_clause_id !== topicClauseId
                    ),
                };
            });
            onChange(updated);
        },
        [topics, onChange]
    );

    const handleRemoveTopic = useCallback(
        (topicId: number) => {
            onRemoveTopic?.(topicId);
        },
        [onRemoveTopic]
    );

    const handleBulkRemove = useCallback(() => {
        const selectedIds = Object.keys(rowSelection).filter(
            (k) => rowSelection[k]
        );

        const removals = new Map<number, Set<number>>();
        for (const id of selectedIds) {
            const [topicIdStr, clauseIdStr] = id.split(":");
            if (clauseIdStr === "sentinel") continue;
            const topicId = Number(topicIdStr);
            const clauseId = Number(clauseIdStr);
            if (!removals.has(topicId)) removals.set(topicId, new Set());
            removals.get(topicId)!.add(clauseId);
        }

        const updated = topics.map((t) => {
            const clauseIdsToRemove = removals.get(t.topic_id);
            if (!clauseIdsToRemove) return t;
            return {
                ...t,
                enabled_clauses: t.enabled_clauses.filter(
                    (ec) => !clauseIdsToRemove.has(ec.topic_clause_id)
                ),
            };
        });

        onChange(updated);
        setRowSelection({});
    }, [topics, onChange, rowSelection]);

    // -- Render --------------------------------------------------------------

    return (
        <div>
            {/* Toolbar */}
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[15px] font-semibold">Topics</span>
                    <span className="text-muted-foreground text-[13px] tabular-nums">
                        {topics.length}
                    </span>
                </div>
                <div className="flex gap-1.5">
                    {selectedCount > 0 && (
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleBulkRemove}
                        >
                            Remove {selectedCount} selected
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={onAddTopic}>
                        <Plus className="mr-1 size-3.5" />
                        Add Topic
                    </Button>
                    <Button variant="outline" size="sm" onClick={onAddClause}>
                        <Plus className="mr-1 size-3.5" />
                        Add Clause
                    </Button>
                </div>
            </div>

            {/* Table card */}
            {topics.length === 0 ? (
                <div className="bg-muted/20 rounded-lg border border-dashed py-12 text-center">
                    <p className="text-muted-foreground text-sm">
                        No topics yet. Add topics or clauses to get started.
                    </p>
                </div>
            ) : (
                <div className="border-border overflow-hidden rounded-lg border">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50 hover:bg-muted/50 border-border border-b">
                                <TableHead className="w-9 py-2" />
                                <TableHead className="text-muted-foreground py-2 text-[12px] font-medium">
                                    Clause
                                </TableHead>
                                <TableHead className="text-muted-foreground w-[200px] py-2 text-[12px] font-medium">
                                    Sections
                                </TableHead>
                                <TableHead className="w-12 py-2" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((row) => {
                                if (row.getIsGrouped()) {
                                    return (
                                        <TopicGroupHeaderRow
                                            key={row.id}
                                            row={row}
                                            meta={topicMetaMap.get(
                                                row.original.topicId
                                            )}
                                            onManage={setManagingTopicId}
                                            onRemove={handleRemoveTopic}
                                        />
                                    );
                                }

                                if (row.original.isSentinel) {
                                    return <SentinelRow key={row.id} />;
                                }

                                return (
                                    <ClauseLeafRow
                                        key={row.id}
                                        row={row}
                                        onToggleSectionType={
                                            handleToggleSectionType
                                        }
                                        onRemoveClause={handleRemoveClause}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Manage disabled clauses dialog */}
            {manageTopic && (
                <ManageDisabledClausesDialog
                    open
                    onClose={() => setManagingTopicId(null)}
                    topic={manageTopic}
                    onEnable={(selections) => {
                        const updated = topics.map((t) =>
                            t.topic_id === managingTopicId
                                ? {
                                      ...t,
                                      enabled_clauses: [
                                          ...t.enabled_clauses,
                                          ...selections,
                                      ],
                                  }
                                : t
                        );
                        onChange(updated);
                    }}
                />
            )}
        </div>
    );
}
